import type { Component } from 'vue'
import {
  BuildingOffice2Icon,
  ChartBarSquareIcon,
  Cog8ToothIcon,
  HomeIcon,
  LifebuoyIcon,
} from '@heroicons/vue/24/outline'
import type { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'

type PermissionStore = ReturnType<typeof useBoostCorePermissionStore>

/**
 * Entrée du menu sidebar V2 (cf design-system/MASTER.md §10).
 *
 * Structure supportée (3 niveaux de données, 2 niveaux interactifs) :
 *   - niveau 1 : lien direct (icône obligatoire) OU hub accordéon (`children`)
 *   - niveau 2 : lien OU groupe titré (`children`) — le groupe n'est pas repliable,
 *     il rend un header `menu-title` + ses liens (= "sous-sous-menu" sans accordéons
 *     imbriqués, conforme RGAA 12.10)
 *   - niveau 3 : liens uniquement
 *
 * Routes mixtes : `to` peut pointer vers une page V1 ou V2 — en mode v2 les deux
 * sont rendues dans le shell ApyLayout. Seules les routes `meta.version: 'v2'`
 * exigent le flag LAYOUT_APY (guard router).
 */
/**
 * Accent de section (niveau 1) — donne une couleur de marque à chaque hub pour
 * les distinguer d'un coup d'œil (cf charte APY). Mappé vers un jeton CSS dans
 * SidebarMenuList (`--apy-*`). La couleur ne véhicule jamais une info seule :
 * elle s'ajoute à l'icône + au libellé (MASTER §1).
 */
export type HubAccent = 'coral' | 'teal' | 'blue' | 'deep' | 'ardoise'

/**
 * Sous-thème par section : couleur (jeton CSS de marque) + libellé lisible
 * (affiché dans le badge du header). Source unique réutilisée par la sidebar,
 * le header et le contenu (via `--hub-accent`).
 */
export const HUB_ACCENTS: Record<HubAccent, { color: string; label: string }> = {
  deep: { color: 'var(--apy-p1)', label: 'Bleu pétrole' },
  teal: { color: 'var(--apy-p3)', label: "Vert d'eau" },
  blue: { color: 'var(--apy-p2)', label: 'Indigo' },
  coral: { color: 'var(--apy-s2)', label: 'Corail' },
  ardoise: { color: 'var(--apy-slate)', label: 'Ardoise' },
}

export interface SidebarMenuEntry {
  /** Clé stable — sert pour `expandedSections` (persisté) et les ids ARIA */
  id: string
  label: string
  /** Heroicon outline — requis au niveau 1 uniquement */
  icon?: Component
  /** Couleur d'accent du hub (niveau 1 uniquement) — distinction visuelle */
  accent?: HubAccent
  /** Path interne (V1 ou V2) — exclusif avec `href` */
  to?: string
  /** Lien externe — ouvre dans un nouvel onglet */
  href?: string
  /**
   * Lien externe dynamique, résolu via le store dans `filterSidebarMenu`
   * (ex: URL Odoo selon la source de l'user). Prioritaire sur `href`.
   */
  resolveHref?: (perms: PermissionStore) => string
  /** Visibilité selon permissions/flags (défaut : visible) */
  visible?: (perms: PermissionStore) => boolean
  children?: SidebarMenuEntry[]
}

export const sidebarMenu: SidebarMenuEntry[] = [
  {
    id: 'accueil',
    label: 'Accueil',
    icon: HomeIcon,
    accent: 'deep',
    to: '/accueil',
  },
  {
    id: 'hub-client',
    label: 'Hub Client',
    icon: BuildingOffice2Icon,
    accent: 'teal',
    children: [
      // Parité V1 : /search (SearchPartnerPage) fait office de liste/recherche
      // clients en attendant une vraie page liste V2.
      { id: 'clients', label: 'Clients', to: '/recherche' },
      {
        id: 'portefeuille-client',
        label: 'Portefeuille Client',
        to: '/commercial/dashboard',
        visible: (p) => p.hasFlag('CUSTOMER_WALLET'),
      },
      { id: 'contacts', label: 'Contacts', to: '/dashboard/contacts' },
      {
        id: 'leads',
        label: 'Leads',
        to: '/dashboard/leads',
        visible: (p) => p.canAccessLeads,
      },
      { id: 'affaires', label: 'Affaires', to: '/opportunity' },
      {
        id: 'commandes',
        label: 'Commandes',
        to: '/dashboard/orders',
        visible: (p) => p.canAccessOrders,
      },
      {
        id: 'eligibilites',
        label: 'Éligibilités',
        to: '/eligibility',
        visible: (p) => p.canAccessEligibility,
      },
      // TODO V2 : "Factures" — pas de route interne aujourd'hui (Odoo), à trancher.
    ],
  },
  {
    id: 'mon-activite',
    label: 'Mon activité',
    icon: ChartBarSquareIcon,
    accent: 'blue',
    children: [
      { id: 'kpi', label: 'KPI', to: '/kpi' },
      { id: 'agenda', label: 'Agenda', to: '/diary' },
      { id: 'todos', label: 'To Do', to: '/dashboard/todos' },
      { id: 'campagnes', label: 'Campagnes', to: '/campaigns' },
      {
        id: 'variables',
        label: 'Variables',
        to: '/variables',
        visible: (p) => p.canAccessVariables,
      },
    ],
  },
  {
    id: 'hub-interne',
    label: 'Hub Interne',
    icon: LifebuoyIcon,
    accent: 'coral',
    children: [
      {
        id: 'tickets',
        label: 'Support interne',
        to: '/tickets',
        // Parité V1 — à élargir quand le périmètre du support interne sera acté
        visible: (p) => p.include('SUPER_ADMIN'),
      },
      {
        // Stats d'appels H3A : outil interne (permission dédiée), pas de l'admin.
        id: 'h3a-stats',
        label: 'Stats H3A',
        to: '/h3a-stats',
        visible: (p) => p.can('H3A_STATS'),
      },
      { id: 'news', label: 'Nouveautés', to: '/news' },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    icon: Cog8ToothIcon,
    accent: 'ardoise',
    children: [
      {
        id: 'admin-acces',
        label: 'Utilisateurs & accès',
        children: [
          {
            id: 'admin-users',
            label: 'Utilisateurs',
            to: '/admin/users',
            visible: (p) => p.include('USER_MASTER'),
          },
        ],
      },
      {
        id: 'admin-operations',
        label: 'Opérations',
        children: [
          {
            id: 'admin-livraisons',
            label: 'Livraisons',
            to: '/dashboard/admin/deliveries',
            visible: (p) => p.can('DELIVERY_ADMIN') || p.include('SUPER_ADMIN'),
          },
          {
            id: 'admin-exports-cegid',
            label: 'Exports Cegid (TRA)',
            to: '/admin/exports-cegid',
            visible: (p) => p.include('SUPER_ADMIN'),
          },
          {
            id: 'admin-reassign-clients',
            label: 'Réassignation clients',
            to: '/admin/reassign-clients',
            visible: (p) => p.include('SUPER_ADMIN'),
          },
          {
            id: 'admin-exports-devis',
            label: 'Export devis (lignes)',
            to: '/admin/exports-devis',
            visible: (p) => p.include('SUPER_ADMIN'),
          },
        ],
      },
    ],
  },
]

/**
 * Filtre récursivement le menu selon les permissions : un parent (hub ou groupe)
 * sans aucun enfant visible disparaît — un user sans droit admin ne voit pas
 * le hub Admin du tout.
 */
export function filterSidebarMenu(
  entries: SidebarMenuEntry[],
  perms: PermissionStore,
): SidebarMenuEntry[] {
  const result: SidebarMenuEntry[] = []
  for (const entry of entries) {
    if (entry.visible && !entry.visible(perms)) continue
    const resolved = entry.resolveHref ? { ...entry, href: entry.resolveHref(perms) } : entry
    if (!resolved.children) {
      result.push(resolved)
      continue
    }
    const children = filterSidebarMenu(resolved.children, perms)
    if (children.length > 0) result.push({ ...resolved, children })
  }
  return result
}

/** Id du hub de niveau 1 contenant le path actif (pour l'auto-dépliage). */
export function findActiveSectionId(
  entries: SidebarMenuEntry[],
  path: string,
): string | undefined {
  const containsPath = (entry: SidebarMenuEntry): boolean => {
    if (entry.to && (path === entry.to || path.startsWith(entry.to + '/'))) return true
    return entry.children?.some(containsPath) ?? false
  }
  return entries.find((e) => e.children && containsPath(e))?.id
}

/**
 * Entrée de niveau 1 (hub OU lien direct comme Accueil) contenant le path actif.
 * Sert à appliquer le sous-thème de section (couleur du header, `--hub-accent`).
 */
export function findActiveHub(
  entries: SidebarMenuEntry[],
  path: string,
): SidebarMenuEntry | undefined {
  const contains = (entry: SidebarMenuEntry): boolean => {
    if (entry.to && (path === entry.to || path.startsWith(entry.to + '/'))) return true
    return entry.children?.some(contains) ?? false
  }
  return entries.find(contains)
}
